import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  /** User-selected theme preference. `'system'` follows OS dark/light mode. */
  theme: ThemeMode;
  /** Resolved theme actually applied to the DOM (`'system'` resolves to `light`/`dark`). */
  resolved: 'light' | 'dark';
  setTheme: (mode: ThemeMode) => void;
  /** Cycle through light → dark → system. Wired to the AppShell toggle button. */
  cycle: () => void;
  /** Re-evaluate `theme` against `prefers-color-scheme` and apply the `.dark` class. */
  apply: () => void;
  /** Idempotent — call once before render to wire up persistence + media-query listener. */
  bootstrap: () => void;
}

const STORAGE_KEY = 'theme';

function readStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* localStorage may be unavailable in private mode — fall through to default */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return mode;
}

function applyToDom(resolved: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  // Hint to the browser for built-in form controls / scrollbars.
  document.documentElement.style.colorScheme = resolved;
}

let mediaListenerAttached = false;

export const useThemeStore = create<ThemeState>()(
  subscribeWithSelector((set, get) => ({
    theme: 'system',
    resolved: 'light',
    setTheme(mode) {
      try {
        window.localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        /* ignore quota / privacy errors */
      }
      const resolved = resolveTheme(mode);
      applyToDom(resolved);
      set({ theme: mode, resolved });
    },
    cycle() {
      const next: Record<ThemeMode, ThemeMode> = {
        light: 'dark',
        dark: 'system',
        system: 'light',
      };
      get().setTheme(next[get().theme]);
    },
    apply() {
      const resolved = resolveTheme(get().theme);
      applyToDom(resolved);
      set({ resolved });
    },
    bootstrap() {
      const stored = readStoredTheme();
      const resolved = resolveTheme(stored);
      applyToDom(resolved);
      set({ theme: stored, resolved });

      // Re-apply when the OS theme flips, but only while the user is in
      // `'system'` mode — otherwise their explicit choice wins.
      if (
        !mediaListenerAttached &&
        typeof window !== 'undefined' &&
        window.matchMedia
      ) {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
          if (get().theme === 'system') get().apply();
        };
        // `addEventListener` is the modern API; older Safari only had `addListener`.
        if (typeof mq.addEventListener === 'function') {
          mq.addEventListener('change', onChange);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (mq as any).addListener(onChange);
        }
        mediaListenerAttached = true;
      }
    },
  }))
);
